import React from 'react';
import { ChevronRight, FilePen, FileUp, QrCode, ArrowRightLeft, Scale } from 'lucide-react';
import { OwnerCard, OwnerInfoList, OwnerShell } from '../OwnerShell';
import { StatusBadge } from '../../common/StatusBadge';
import { PersonPhoto } from '../../common/PersonPhoto';
import { useMyVehicle } from '../../../hooks/useMyVehicles';
import { getDriver } from '../../../data/drivers';
import { docTone, ownerDocuments, ownerProfile, roadsideHistory } from '../../../data/ownerProfile';
import { ownerVehicleStatus, stickerStatus } from '../../../utils/ownerStatus';
import { toBn } from '../../../utils/bn';
import type { OwnerNav, OwnerScreen } from '../../../types/owner';
const tabs = [{
  key: 'info',
  label: 'গাড়ির তথ্য'
}, {
  key: 'owner',
  label: 'মালিকের তথ্য'
}, {
  key: 'drivers',
  label: 'চালকের তথ্য'
}, {
  key: 'docs',
  label: 'ডকুমেন্ট'
}, {
  key: 'qr',
  label: 'QR স্টিকার'
}, {
  key: 'history',
  label: 'যাচাই ইতিহাস'
}];
function LinkRow({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return <button onClick={onClick} className="mt-3 flex w-full items-center justify-between rounded-lg bg-navy-50 px-3 py-2.5 text-[13px] font-semibold text-navy-800" data-id="element-2068" data-mp="22uppo87epub0">
      {label}
      <ChevronRight className="h-4 w-4" data-id="element-2069" data-mp="2bsrvr25zzd55" />
    </button>;
}
export function OwnerVehicleDetail({
  nav
}: {
  nav: OwnerNav;
}) {
  const mv = useMyVehicle(nav.vehicleId);
  if (!mv) return null;
  const {
    vehicle: v,
    app,
    driverIds
  } = mv;
  const st = ownerVehicleStatus(app.status);
  const qr = stickerStatus(app.stage);
  const docs = ownerDocuments[v.id] ?? [];
  const history = roadsideHistory[v.id] ?? [];
  const tab = nav.detailTab;
  const open = (s: OwnerScreen) => nav.go(s);
  return <OwnerShell nav={nav} title="গাড়ির বিস্তারিত তথ্য" bodyClassName="p-0" data-id="element-2070" data-mp="2zici7v1d0sj6">
      <div className="relative h-40" data-id="element-2071" data-mp="34j0h12vtydny">
        <img src={v.image} alt={v.reg} className="h-full w-full object-cover" data-id="element-2072" data-mp="34k1vzifyvkay" />
        <div className="absolute inset-x-0 bottom-0 bg-navy-900/80 px-4 py-2.5 text-white" data-id="element-2073" data-mp="39imtvb7qxrw8">
          <p className="font-heading text-lg font-bold" data-id="element-2074" data-mp="19ek9pyazon0j">{v.reg}</p>
          <p className="text-[11px] text-navy-100" data-id="element-2075" data-mp="14g95c33gr4pp">{v.typeLabel} • {v.brand} {v.model}</p>
        </div>
      </div>
      <div className={`flex items-center justify-between px-4 py-2.5 ${st.tone === 'green' ? 'bg-emerald-50' : 'bg-amber-50'}`} data-id="element-2076" data-mp="1j5ndx8z7ti41">
        <span className="text-[13px] font-semibold text-navy-900" data-id="element-2077" data-mp="1edn6ywkq9oxy">যাচাই অবস্থা</span>
        <StatusBadge tone={st.tone} data-id="element-2078" data-mp="1s51zpv4ft4i7">{st.tone === 'green' ? '✓ ' : ''}{st.label}</StatusBadge>
      </div>

      <div className="no-scrollbar sticky top-0 z-10 flex gap-1 overflow-x-auto border-b border-border bg-white px-2" role="tablist" data-id="element-2079" data-mp="1o36s68vwxuvz">
        {tabs.map(t => <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => nav.setDetailTab(t.key)} className={`shrink-0 border-b-2 px-2.5 py-2.5 text-[12.5px] font-semibold ${tab === t.key ? 'border-navy-800 text-navy-900' : 'border-transparent text-slate-500'}`} data-id="element-2080" data-mp="1x568eyz3suxv">
            {t.label}
          </button>)}
      </div>

      <div className="space-y-3 px-4 py-4" data-id="element-2081" data-mp="2efjwdqtdich8">
        {tab === 'info' && <OwnerCard data-id="element-2082" data-mp="2jdv9qbivn547">
            <OwnerInfoList rows={[['রেজিস্ট্রেশন নম্বর', v.reg], ['গাড়ির ধরন', v.type === 'cng' ? 'সিএনজি অটোরিকশা' : 'মোটরসাইকেল'], ['ব্র্যান্ড ও মডেল', `${v.brand} ${v.model}`], ['রং', v.color], ['তৈরির সাল', v.year], ['চেসিস নম্বর', <span key="c" className="font-mono text-[12px]" data-id="element-2084" data-mp="1vpu0f0dvjknq">{v.chassis}</span>], ['ইঞ্জিন নম্বর', <span key="e" className="font-mono text-[12px]" data-id="element-2085" data-mp="20khiagedhgtc">{v.engine}</span>], ['জ্বালানি', v.fuel], ['নিবন্ধনের তারিখ', v.regDate], ['মালিকানার ধরন', v.ownership], ['আবেদন আইডি', <span key="a" className="font-mono text-[11.5px]" data-id="element-2086" data-mp="3g32uvifl3c7l">{app.id}</span>]]} data-id="element-2083" data-mp="2jc0kwm4hisuu" />
          </OwnerCard>}
        {tab === 'owner' && <OwnerCard data-id="element-2087" data-mp="3l0bd1j0qfg8u">
            <div className="mb-2 flex items-center gap-3" data-id="element-2088" data-mp="1l0y1dktk44ev">
              <img src={ownerProfile.photo} alt="" className="h-12 w-12 rounded-full object-cover" data-id="element-2089" data-mp="1kybu7makvqx6" />
              <div data-id="element-2090" data-mp="1gwq9jersy8cf">
                <p className="font-semibold text-navy-900" data-id="element-2091" data-mp="1bzrfew8521e8">{ownerProfile.name}</p>
                <StatusBadge tone="green" className="!text-[10.5px]" data-id="element-2092" data-mp="1c1uee5r9r4v3">মালিকানা সক্রিয়</StatusBadge>
              </div>
            </div>
            <OwnerInfoList rows={[['NID', ownerProfile.nid], ['মোবাইল', ownerProfile.mobile], ['ঠিকানা', 'মিরপুর, ঢাকা'], ['BRTA মালিকানা তথ্য', app.brtaResult === 'মিলেছে' ? '✓ যাচাইকৃত' : 'যাচাই অপেক্ষমাণ']]} data-id="element-2093" data-mp="1223nee1x37zr" />
            <LinkRow label="মালিকানা পরিবর্তন সংক্রান্ত আবেদন" onClick={() => open('ownership')} data-id="element-2094" data-mp="t8gq2ji29wog" />
          </OwnerCard>}
        {tab === 'drivers' && <OwnerCard title={v.type === 'cng' ? `অনুমোদিত চালক (${toBn(driverIds.length)})` : 'মালিক / রাইডার'} data-id="element-2095" data-mp="jb2ofq0srcg7">
            {v.type === 'cng' ? <ul className="space-y-2" data-id="element-2096" data-mp="edams4acobyt">
                {driverIds.map(id => {
            const d = getDriver(id);
            return d ? <li key={id} className="flex items-center gap-2.5" data-id="element-2097" data-mp="96vr5z619082">
                      <PersonPhoto src={d.photo} name={d.name} size={36} data-id="element-2098" data-mp="998e6qqne0rz" />
                      <div className="flex-1" data-id="element-2099" data-mp="e6rstrr9cfkg"><p className="text-[13px] font-semibold text-navy-900" data-id="element-2100" data-mp="e45qq7n4ys9m">{d.name}</p><p className="font-mono text-[10.5px] text-slate-500" data-id="element-2101" data-mp="j40gg7v6dbt3">{d.license}</p></div>
                      <StatusBadge tone="green" className="!text-[10px]" data-id="element-2102" data-mp="3hoa09y34bkkc">সক্রিয়</StatusBadge>
                    </li> : null;
          })}
              </ul> : <p className="text-[13px] text-slate-600" data-id="element-2103" data-mp="3rkd2t51vtl7t">{ownerProfile.name} (মালিক নিজেই রাইডার)</p>}
            <LinkRow label="চালকের তথ্য ও আবেদন" onClick={() => open('drivers')} data-id="element-2104" data-mp="80imgfine1l" />
          </OwnerCard>}
        {tab === 'docs' && <OwnerCard data-id="element-2105" data-mp="33vani3uq04gh">
            <ul className="divide-y divide-slate-100" data-id="element-2106" data-mp="33ur2ufdor1yr">
              {docs.map(d => {
            const t = docTone(d.daysLeft);
            return <li key={d.key} className="flex items-center justify-between gap-2 py-2 text-[13px]" data-id="element-2107" data-mp="2qvrpu1v47lkf">
                    <span className="text-navy-900" data-id="element-2108" data-mp="35ko4ihrofwu0">{d.name}</span>
                    <StatusBadge tone={t.tone} className="!text-[10.5px]" data-id="element-2109" data-mp="35jw9b81zs149">{t.label}</StatusBadge>
                  </li>;
          })}
              {docs.length === 0 && <li className="py-3 text-center text-xs text-slate-500" data-id="element-2110" data-mp="2cws0st3ox4nq">আবেদনের ডকুমেন্ট ডিএমপি পর্যালোচনাধীন</li>}
            </ul>
            <LinkRow label="সকল কাগজপত্র ও মেয়াদ" onClick={() => open('docTracker')} data-id="element-2111" data-mp="27z0a22es7ol4" />
          </OwnerCard>}
        {tab === 'qr' && <OwnerCard data-id="element-2112" data-mp="2huk0waehnx30">
            <OwnerInfoList rows={[['QR ID', <span key="q" className="font-mono text-[11.5px]" data-id="element-2114" data-mp="2hx6ccl4i2wqi">{app.stage >= 6 ? v.qrId : '—'}</span>], ['স্টিকার অবস্থা', <StatusBadge key="s" tone={qr.tone} className="!text-[10.5px]" data-id="element-2115" data-mp="25s8im5tvrhm">{qr.label}</StatusBadge>], ['ইস্যুর তারিখ', v.stickerIssued ?? '—']]} data-id="element-2113" data-mp="2huad8tgx220h" />
            <LinkRow label="QR স্টিকার দেখুন" onClick={() => open('qr')} data-id="element-2116" data-mp="4nc2knk2wqcs" />
          </OwnerCard>}
        {tab === 'history' && <OwnerCard data-id="element-2117" data-mp="9ldkv69okln7">
            {history.length ? <ul className="divide-y divide-slate-100" data-id="element-2118" data-mp="3ru4pq7voqgas">
                {history.slice(0, 3).map(h => <li key={h.date + h.time} className="flex items-center justify-between py-2 text-[12.5px]" data-id="element-2119" data-mp="3vvg6rp0htt7d">
                    <span data-id="element-2120" data-mp="nfepggnbd2vh"><span className="font-semibold text-navy-900" data-id="element-2121" data-mp="nfelk5k70iq7">{h.date}</span> <span className="text-slate-500" data-id="element-2122" data-mp="se7r9bt8n5ft">• {h.division}</span></span>
                    <StatusBadge tone={h.result === 'বৈধ' ? 'green' : 'orange'} className="!text-[10.5px]" data-id="element-2123" data-mp="emt6azfrsu64">{h.result}</StatusBadge>
                  </li>)}
              </ul> : <p className="py-3 text-center text-xs text-slate-500" data-id="element-2124" data-mp="16d7fwjgr3kbz">স্টিকার ইস্যুর পর রোডসাইড যাচাইয়ের তথ্য এখানে দেখা যাবে।</p>}
            <LinkRow label="সম্পূর্ণ যাচাই ইতিহাস" onClick={() => open('history')} data-id="element-2125" data-mp="1b60ib0oqygkw" />
          </OwnerCard>}

        <div className="grid grid-cols-3 gap-2 pt-1" data-id="element-2126" data-mp="mfw8z7wl2wdn">
          {[{
          l: 'তথ্য সংশোধনের আবেদন',
          i: FilePen,
          s: 'correction' as OwnerScreen,
          prep: () => nav.setCorrectionPrefill('')
        }, {
          l: 'ডকুমেন্ট হালনাগাদ',
          i: FileUp,
          s: 'docTracker' as OwnerScreen
        }, {
          l: 'QR স্টিকার দেখুন',
          i: QrCode,
          s: 'qr' as OwnerScreen
        }].map(a => <button key={a.l} onClick={() => {
          a.prep?.();
          open(a.s);
        }} className="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-white px-1 py-3 text-center text-[11px] font-semibold leading-tight text-navy-800" data-id="element-2127" data-mp="15c5kpok0jauh">
              <a.i className="h-5 w-5" data-id="element-2128" data-mp="qfev27055di2" />
              {a.l}
            </button>)}
        </div>
        <div className="grid grid-cols-2 gap-2" data-id="element-2129" data-mp="3g4p3sg15qhgm">
          <button onClick={() => open('notices')} className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-white py-2.5 text-[12px] font-medium text-slate-700" data-id="element-2130" data-mp="3g6a28gqlekjr"><Scale className="h-4 w-4" data-id="element-2131" data-mp="1qomlp9glo518" />ট্রাফিক নোটিশ</button>
          <button onClick={() => open('ownership')} className="flex items-center justify-center gap-1.5 rounded-xl border border-border bg-white py-2.5 text-[12px] font-medium text-slate-700" data-id="element-2132" data-mp="1vmo4rue104cp"><ArrowRightLeft className="h-4 w-4" data-id="element-2133" data-mp="20joc08mm3d5e" />মালিকানা পরিবর্তন</button>
        </div>
      </div>
    </OwnerShell>;
}